import {useVisitsStore} from '../store/visitsStore';
import {PAGINATION_CONFIG} from '../../../constants/appConfig';

/**
 * Main Visits Hook - Dashboard Visits Module
 * يوفر واجهة موحدة للتعامل مع جميع عمليات الزيارات
 * لا يستدعي fetch تلقائياً — الصفحة تستخدم useVisitsManagement الذي يدير الاستدعاءات
 */
export function useVisits () {
  const store = useVisitsStore();

  const handlePageChange = (newPage: number) => {
    const currentLimit = store.pagination?.limit || PAGINATION_CONFIG.DEFAULT_PAGE_SIZE;
    store.fetchVisits(store.filters, newPage, currentLimit);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    store.fetchVisits(store.filters, 1, newPageSize);
  };

  return {
    // Data
    visits: store.visits,
    visitDetails: store.visitDetails,
    statistics: store.statistics,
    trends: store.trends,
    pagination: store.pagination,

    // Filters
    filters: store.filters,

    // Loading states
    loading: store.loading,
    loadingDetails: store.loadingDetails,
    loadingStatistics: store.loadingStatistics,
    loadingTrends: store.loadingTrends,

    // Error states
    error: store.error,
    detailsError: store.detailsError,
    statisticsError: store.statisticsError,
    trendsError: store.trendsError,

    // Actions
    fetchVisits: store.fetchVisits,
    fetchVisitDetails: store.fetchVisitDetails,
    fetchStatistics: store.fetchStatistics,
    fetchTrends: store.fetchTrends,

    // Management actions removed - dashboard is for viewing only

    // Utility actions
    setFilters: store.setFilters,
    setPagination: store.setPagination,
    refreshData: store.refreshData,
    clearErrors: store.clearErrors,
    clearVisitDetails: store.clearVisitDetails,

    // Pagination handlers
    handlePageChange,
    handlePageSizeChange
  };
}

/**
 * Hook for Visit Details Only
 * مفيد عندما تحتاج فقط لتفاصيل زيارة واحدة
 */
export function useVisitDetails (_visitId?: string | number) {
  const {visitDetails, loadingDetails, detailsError, fetchVisitDetails, clearVisitDetails} = useVisitsStore();

  return {
    visitDetails,
    loadingDetails,
    detailsError,
    fetchVisitDetails,
    clearVisitDetails
  };
}

/**
 * Hook for Visit Statistics Only
 * مفيد عندما تحتاج فقط لإحصائيات الزيارات
 */
export function useVisitStatistics () {
  const {statistics, loadingStatistics, statisticsError, fetchStatistics} = useVisitsStore();

  return {
    statistics,
    loadingStatistics,
    statisticsError,
    fetchStatistics
  };
}

/**
 * Hook for Visit Analytics/Trends — يستخدم trends (نفس بيانات التحليلات)
 */
export function useVisitAnalytics () {
  const {trends, loadingTrends, trendsError, fetchTrends} = useVisitsStore();
  return {analytics: trends, loadingAnalytics: loadingTrends, analyticsError: trendsError, fetchAnalytics: fetchTrends};
}

/**
 * Hook for Visit Trends Only
 * مفيد عندما تحتاج فقط لاتجاهات الزيارات
 * Uses store directly to avoid triggering useVisits' fetchVisits (prevents duplicate API calls)
 */
export function useVisitTrends () {
  const {trends, loadingTrends, trendsError, fetchTrends} = useVisitsStore();

  return {
    trends,
    loadingTrends,
    trendsError,
    fetchTrends
  };
}

/**
 * Hook for Visit Management Actions
 * مفيد عندما تحتاج فقط لإجراءات إدارة الزيارات
 */
// useVisitManagement removed - management functionality not needed in dashboard
